import { Component, OnInit } from '@angular/core';
import { DashboardService } from 'src/app/dashboard/dashboard.service';
import { SystemTemplateService } from 'src/app/dashboard/system-templates/system-template.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { WorkspaceNewTabViewMode, WorkspaceTemplateViewMode } from 'src/shared/models/dashboard.model';
import { IntegrationDTO, SystemTemplateModel } from 'src/shared/models/system-template.models';

@Component({
    selector: 'cv-new-system-template',
    templateUrl: './new-system-template.component.html',
    styleUrls: ['./new-system-template.component.scss']
})
export class NewSystemTemplateComponent implements OnInit {
    isSaving = false;
    saveTooltip = '';
    dashboardKey: string;
    personaList: { key: number; name: string }[] = [];
    integrationFilter = '';
    personaFilter = '';
    integrationList: IntegrationDTO[] = [];
    newSystemTemplate: SystemTemplateModel = {
        name: '',
        group: '',
        description: '',
        integrations: [],
        personas: [],
        key: '',
        selectedDomains: '',
        userType: '',
        status: 0,
        lastModifiedDate: '',
        lastModifiedBy: '',
        permissionName: ''
    };

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private dashboardService: DashboardService,
        private systemTemplateService: SystemTemplateService,
    ) {
        this.newSystemTemplate.name = systemTemplateService.systemTemplateInfo.name;
        this.newSystemTemplate.group = systemTemplateService.systemTemplateInfo.group;
        this.dashboardKey = systemTemplateService.systemTemplateInfo.dashboardKey;

        cvLocalizationService.GetSection("Left_Nav_Popup");
        this.loadPersonas();
        this.loadIntegrations();
    }

    ngOnInit() {

    }

    shouldDisableSave() {
        this.saveTooltip = '';
        if (this.isSaving || !this.dashboardKey)
            return true;

        const missingRequiredFields = (
            !this.newSystemTemplate.name
            || !this.newSystemTemplate.group
            || this.newSystemTemplate.personas.length === 0
            || this.newSystemTemplate.integrations.length === 0
        );

        if (missingRequiredFields) {
            this.saveTooltip = this.cvLocalizationService.getCachedText(
                "Left_Nav_Popup",
                "PersonID_Name_And_Group_Required",
                "System Template PersonaID, Name, Integration and Group are required fields."
            );
            return true;
        }

        return false;
    }


    matchesFilter(integration: IntegrationDTO) {
        return integration.IntegrationName.toLocaleLowerCase().includes(this.integrationFilter.toLocaleLowerCase());
    }

    selectAllIntegrations() {
        if (this.integrationFilter) {
            // add all currently filtered integrations to the current selected list, but remove duplicates
            const filteredIntegrations = this.integrationList.filter(i => this.matchesFilter(i)).map(i => i.IntegrationID);
            Array.prototype.push.apply(filteredIntegrations, this.newSystemTemplate.integrations);
            this.newSystemTemplate.integrations = [...new Set(filteredIntegrations)];
        } else {
            this.newSystemTemplate.integrations = this.integrationList.map(i => i.IntegrationID);
        }
    }

    selectNoneIntegrations() {
        if (this.integrationFilter) {
            // remove the filtered integrations from the selected list
            const filteredIntegrations = new Set(this.integrationList.filter(i => this.matchesFilter(i)).map(i => i.IntegrationID));
            this.newSystemTemplate.integrations = this.newSystemTemplate.integrations.filter(i => !filteredIntegrations.has(i));
        } else {
            this.newSystemTemplate.integrations = [];
        }
    }

    matchesPersonaFilter(persona: { key: number; name: string }) {
        return persona.name.toLocaleLowerCase().includes(this.personaFilter.toLocaleLowerCase());
    }

    selectAllPersonas() {
        if (this.personaFilter) {
            // add all currently filtered integrations to the current selected list, but remove duplicates
            const filteredPersonas = this.personaList.filter(p => this.matchesPersonaFilter(p)).map(p => p.key);
            Array.prototype.push.apply(filteredPersonas, this.newSystemTemplate.personas);
            this.newSystemTemplate.personas = [...new Set(filteredPersonas)];
        } else {
            this.newSystemTemplate.personas = this.personaList.map(p => p.key);
        }
    }

    selectNonePersonas() {
        if (this.personaFilter) {
            // remove the filtered integrations from the selected list
            const filteredPersonas = new Set(this.personaList.filter(p => this.matchesPersonaFilter(p)).map(p => p.key));
            this.newSystemTemplate.personas = this.newSystemTemplate.personas.filter(p => !filteredPersonas.has(p));
        } else {
            this.newSystemTemplate.personas = [];
        }
    }

    clearTemplateInfo() {
        this.systemTemplateService.systemTemplateInfo.name = "";
        this.systemTemplateService.systemTemplateInfo.group = "";
        this.systemTemplateService.systemTemplateInfo.dashboardKey = "";
    }

    async loadPersonas() {
        const rolesLocalization = await this.cvLocalizationService.GetSection("Roles");
        this.personaList = [
            { key: 1, name: `1. ${rolesLocalization.get("Agent")}` },
            { key: 2, name: `2. ${rolesLocalization.get("Supervisor")}` },
            { key: 3, name: `3. ${rolesLocalization.get("Admin")}` },
            { key: 4, name: `4. ${rolesLocalization.get("Exec")}` },
        ];
    }

    async loadIntegrations() {
        const response = await this.systemTemplateService.loadIntegrationList();
        this.integrationList = response.data.sort((a, b) =>
            a.IntegrationName.localeCompare(b.IntegrationName, undefined, { sensitivity: 'base' })
        );
    }

    async save() {
        if (this.isSaving)
            return;

        if (this.shouldDisableSave())
            return;

        this.isSaving = true;

        try {
            await this.systemTemplateService.saveNewSystemTemplate(this.dashboardKey, this.newSystemTemplate);
            this.dashboardService.loadSystemTemplates(true, false);
            this.clearTemplateInfo();
            this.dashboardService.newTabSelectionView = WorkspaceNewTabViewMode.Existing;
            this.dashboardService.templateView = WorkspaceTemplateViewMode.Landing;
        } catch(error) {
            console.error(error);
        } finally {
            this.isSaving = false;
        }
    }

    cancel() {
        this.clearTemplateInfo();
        this.dashboardService.newTabSelectionView = WorkspaceNewTabViewMode.Existing;
        this.dashboardService.templateView = WorkspaceTemplateViewMode.Landing;
    }
}
